import { useState, VFC } from "react";

import { Collapse, TextInput } from "@portex-pro/ui-components";
import FormStringConcat from "@portex-pro/ui-components/Forms/FormStringConcat";
import classNames from "classnames";
import PositiveNumberInput from "components/PositiveNumberInput";
import Text from "components/Text";
import YesNoRadio from "components/YesNoRadio";
import { useTranslation } from "react-i18next";
import OptionalMaybe from "types/OptionalMaybe";

import FormFuelButtonGroupView from "../../extra-details/components/FormFuelButtonGroupView";

type BidAttributesViewProps = {
  showFuelSection?: boolean;
  commodities: string;
  isHazardous: OptionalMaybe<boolean>;
  hazardousDetails: OptionalMaybe<string>;
  includeFuel: OptionalMaybe<boolean>;
  hidePoTillDispatch: OptionalMaybe<boolean>;
  hideInternalRefTillDispatch: OptionalMaybe<boolean>;
  uploadExistingPricing: OptionalMaybe<boolean>;
  onChangeCommodities: (val: string) => void;
  onChangeHazardous: (val: boolean) => void;
  onChangeHazardousDetails: (val: string) => void;
  onChangeFuel: (val: boolean) => void;
  onChangeHidePoTillDispatch: (val: boolean) => void;
  onChangeHideInternalRefTillDispatch: (val: boolean) => void;
  onChangeUploadExistingPricing: (val: boolean) => void;

  numberOfStops: number;
  onChangeStops: (numberOfStops: number) => void;
};

const sectionStyles = "grid grid-cols-subgrid col-span-3 px-4 py-4 space-y-3";
const greySectionAdditionalStyles = "bg-grey-50";
const whiteSectionAdditionalStyles = "bg-white";

const BidAttributesView: VFC<BidAttributesViewProps> = ({
  commodities,
  isHazardous,
  hazardousDetails,
  includeFuel,
  hidePoTillDispatch,
  hideInternalRefTillDispatch,
  uploadExistingPricing,
  onChangeCommodities,
  onChangeHazardous,
  onChangeHazardousDetails,
  onChangeFuel,
  onChangeHidePoTillDispatch,
  onChangeHideInternalRefTillDispatch,
  onChangeUploadExistingPricing,
  showFuelSection,
  numberOfStops,
  onChangeStops,
}) => {
  const { t } = useTranslation(["shipper", "common"]);
  const [isMultipleStops, setIsMultipleStops] = useState(numberOfStops > 1);

  return (
    <div className="w-full h-full">
      <div className="flex flex-col items-center py-4 gap-4">
        <Text size="x-large" weight="bold">
          {t("shipper:bids.bidRequestPage.pageBidAttributes.title")}
        </Text>
        <Text size="medium">{t("shipper:bids.bidRequestPage.pageBidAttributes.description")}</Text>
      </div>
      <div className={classNames("w-full grid grid-cols-3 gap-x-4 gap-y-1")}>
        <div className={classNames(sectionStyles, greySectionAdditionalStyles)}>
          <div className="col-span-3">
            <Text
              size="medium"
              weight="bold"
              typographyProps={{ color: "textSecondary", style: { textTransform: "uppercase" } }}
            >
              {t("shipper:bids.bidRequestPage.pageBidAttributes.loadsAndEquipment")}
            </Text>
          </div>

          <div className="flex flex-col">
            <Text size="medium" weight="bold">
              {t("shipper:bids.bidRequestPage.pageBidAttributes.commodities_label")}
            </Text>
            <FormStringConcat margin="normal" value={commodities} onChange={onChangeCommodities} />
          </div>

          <div className="flex flex-col">
            <Text size="medium" weight="bold" required>
              {t("shipper:bids.bidRequestPage.pageBidAttributes.hazardous_label")}
            </Text>
            <YesNoRadio
              value={isHazardous}
              onChange={onChangeHazardous}
              radioGroupProps={{
                style: { flexDirection: "row-reverse", justifyContent: "flex-end", margin: "1rem" },
              }}
            />
          </div>

          <Collapse in={showFuelSection}>
            <div className="flex flex-col">
              <Text size="medium" weight="bold" required>
                {t("shipper:bids.bidRequestPage.pageBidAttributes.fuel_label")}
              </Text>
              <FormFuelButtonGroupView hideFormLabel value={includeFuel} onChange={onChangeFuel} />
            </div>
          </Collapse>

          <Collapse className="col-span-3" in={!!isHazardous}>
            <div className="flex flex-col">
              <Text size="medium" weight="bold">
                {t("shipper:bids.bidRequestPage.pageBidAttributes.hazardousDetails_label")}
              </Text>
              <TextInput
                margin="normal"
                fullWidth
                multiline
                rows={2}
                rowsMax={8}
                value={hazardousDetails}
                onChange={(event) => onChangeHazardousDetails(event.target.value)}
                placeholder={t("shipper:bids.bidRequestPage.pageBidAttributes.hazardousDetails_placeholder")}
              />
            </div>
          </Collapse>
        </div>

        <div className={classNames(sectionStyles, whiteSectionAdditionalStyles)}>
          <div className="col-span-3">
            <Text
              size="medium"
              weight="bold"
              typographyProps={{ color: "textSecondary", style: { textTransform: "uppercase" } }}
            >
              {t("shipper:bids.bidRequestPage.pageBidAttributes.packingInformation")}
            </Text>
          </div>

          <div className="flex flex-col">
            <Text size="medium" weight="bold" required>
              {t("shipper:bids.bidRequestPage.pageBidAttributes.multipleStopsLabel")}
            </Text>
            <YesNoRadio
              value={isMultipleStops}
              onChange={(yes) => {
                if (yes) {
                  setIsMultipleStops(true);
                } else {
                  onChangeStops(1);
                  setIsMultipleStops(false);
                }
              }}
              radioGroupProps={{
                style: { flexDirection: "row-reverse", justifyContent: "flex-end", margin: "1rem" },
              }}
            />

            <Collapse in={isMultipleStops}>
              <div className="flex flex-col">
                <Text size="medium" weight="bold">
                  {t("shipper:bids.bidRequestPage.pageBidAttributes.maxStopsLabel")}
                </Text>
                <PositiveNumberInput value={numberOfStops} onChange={onChangeStops} allowEmpty disableError />
              </div>
            </Collapse>
          </div>

          <div>
            <Text size="medium" weight="bold" required>
              {t("shipper:bids.bidRequestPage.pageBidAttributes.hidePoTillDispatch")}
            </Text>
            <YesNoRadio
              value={hidePoTillDispatch ?? false}
              onChange={(val) => onChangeHidePoTillDispatch(val)}
              radioGroupProps={{
                style: { flexDirection: "row-reverse", justifyContent: "flex-end", margin: "1rem" },
              }}
            />
          </div>

          <div>
            <Text size="medium" weight="bold" required>
              {t("shipper:bids.bidRequestPage.pageBidAttributes.hideInternalRefTillDispatch")}
            </Text>
            <YesNoRadio
              value={hideInternalRefTillDispatch ?? false}
              onChange={(val) => onChangeHideInternalRefTillDispatch(val)}
              radioGroupProps={{
                style: { flexDirection: "row-reverse", justifyContent: "flex-end", margin: "1rem" },
              }}
            />
          </div>
          <div>
            <Text size="medium" weight="bold" required>
              {t("shipper:bids.bidRequestPage.pageBidAttributes.uploadExistingPricing")}
            </Text>
            <YesNoRadio
              value={uploadExistingPricing ?? false}
              onChange={(val) => onChangeUploadExistingPricing(val)}
              radioGroupProps={{
                style: { flexDirection: "row-reverse", justifyContent: "flex-end", margin: "1rem" },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BidAttributesView;
